import React, { useEffect, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  Delete as DeleteIcon,
  FileCopy as FileCopyIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'
import BaseDialog from 'components/BaseDialog'
import ConfirmationDialog from 'components/ConfirmationDialog'
import LoadingFeedback from 'components/LoadingFeedback'
import Permitted from 'components/Permitted'
import useSnackbar from 'hooks/useSnackbar'
import usePermissions from 'hooks/usePermissions'

import * as service from 'service'
import helpers from 'helpers'
import theme from 'theme'
import useStyles from './styles'

const ExternalLinkDialog = ({ open, setOpen, module, managePermission }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [config, setConfig] = useState(null)
  const [emails, setEmails] = useState([])
  const [active, setActive] = useState(true)
  const [expirationDays, setExpirationDays] = useState(365)
  const [newEmail, setNewEmail] = useState('')
  const [openAddEmail, setOpenAddEmail] = useState(false)
  const [domains, setDomains] = useState([])
  const [newDomain, setNewDomain] = useState('')
  const [openAddDomain, setOpenAddDomain] = useState(false)
  const [domainToRemove, setDomainToRemove] = useState(null)
  const [emailToRemove, setEmailToRemove] = useState(null)

  const classes = useStyles()
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()

  const canManage = permitted(managePermission)

  const portalLink = config?.token
    ? helpers.guestPortal.buildJourneyUrl(
        config.token,
        config.organizationSlug,
        module,
      )
    : null

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [configResponse, emailsResponse, domainsResponse] =
        await Promise.allSettled([
          service.dponet.companyGuestPortalConfig.show(module),
          service.dponet.companyGuestEmails.get(module, {}),
          service.dponet.companyGuestDomains.get(module, {}),
        ])

      if (configResponse.status === 'fulfilled') {
        const loaded = configResponse.value?.data?.companyGuestPortalConfig
        setConfig(loaded)
        setActive(loaded?.active ?? true)
        setExpirationDays(loaded?.expirationDays ?? 365)
      }

      if (emailsResponse.status === 'fulfilled') {
        setEmails(emailsResponse.value?.data?.companyGuestEmails || [])
      }

      if (domainsResponse.status === 'fulfilled') {
        setDomains(domainsResponse.value?.data?.companyGuestDomains || [])
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = async () => {
    setIsSaving(true)
    try {
      const response = await service.dponet.companyGuestPortalConfig.create({
        module,
        active,
        expirationDays,
      })
      setConfig(response?.data?.companyGuestPortalConfig)
      snackbar.open({
        message: 'Portal criado com sucesso.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao criar o portal.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await service.dponet.companyGuestPortalConfig.update(module, {
        active,
        expirationDays,
      })
      snackbar.open({
        message: 'Configurações salvas com sucesso.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao salvar configurações.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleRegenerateToken = async () => {
    setIsSaving(true)
    try {
      const response =
        await service.dponet.companyGuestPortalConfig.regenerateToken(module)
      setConfig(response?.data?.companyGuestPortalConfig)
      snackbar.open({
        message: 'Novo link gerado. O link anterior foi invalidado.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao gerar novo link.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(portalLink)
    snackbar.open({ message: 'Link copiado!', variant: 'success' })
  }

  const handleAddEmail = async () => {
    if (!newEmail.trim() || isSaving) return

    setIsSaving(true)
    try {
      const response = await service.dponet.companyGuestEmails.create(module, {
        email: newEmail.trim(),
      })
      setEmails((prev) => [...prev, response?.data?.companyGuestEmail])
      setNewEmail('')
      setOpenAddEmail(false)
      snackbar.open({ message: 'E-mail adicionado.', variant: 'success' })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao adicionar e-mail.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmRemoveEmail = async () => {
    if (!emailToRemove || isSaving) return

    setIsSaving(true)
    try {
      await service.dponet.companyGuestEmails.destroy(module, emailToRemove.id)
      setEmails((prev) => prev.filter((email) => email.id !== emailToRemove.id))
      setEmailToRemove(null)
      snackbar.open({ message: 'E-mail removido.', variant: 'success' })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao remover e-mail.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddDomain = async () => {
    if (isSaving) return

    const { normalized, error } = helpers.guestPortal.validateDomain(
      newDomain,
      domains.map((item) => item.domain),
    )

    if (error) {
      snackbar.open({ message: error, variant: 'error' })
      return
    }

    setIsSaving(true)
    try {
      const response = await service.dponet.companyGuestDomains.create(module, {
        domain: normalized,
      })
      const createdDomain = response?.data?.companyGuestDomain

      if (!createdDomain) {
        snackbar.open({
          message: 'Erro ao adicionar domínio.',
          variant: 'error',
        })
        return
      }

      setDomains((prev) => [...prev, createdDomain])
      setNewDomain('')
      setOpenAddDomain(false)
      snackbar.open({ message: 'Domínio adicionado.', variant: 'success' })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao adicionar domínio.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfirmRemoveDomain = async () => {
    if (!domainToRemove || isSaving) return

    setIsSaving(true)
    try {
      await service.dponet.companyGuestDomains.destroy(
        module,
        domainToRemove.id,
      )
      setDomains((prev) =>
        prev.filter((domain) => domain.id !== domainToRemove.id),
      )
      setDomainToRemove(null)
      snackbar.open({ message: 'Domínio removido.', variant: 'success' })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao remover domínio.',
        variant: 'error',
      })
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    if (open) loadData()
    // eslint-disable-next-line
  }, [open])

  return (
    <>
      <LoadingFeedback open={isSaving} />
      <BaseDialog
        open={open}
        setOpen={setOpen}
        title="Portal de Visitantes — Link Externo"
        closeButtonText="Fechar"
        actionButtonText={
          canManage
            ? config
              ? 'Salvar configurações'
              : 'Criar link externo'
            : ''
        }
        actionButton={config ? handleSave : handleCreate}
        disableButton={isSaving}
        loading={isSaving}
        dialogSize="md"
        fullWidth
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box className={classes.settingsCard} mb={2}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body1" className={classes.settingLabel}>
                    Status do portal
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Controla se o link está disponível para visitantes
                  </Typography>
                </Box>
                <Switch
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                  color="primary"
                  disabled={!canManage}
                />
              </Box>

              <Box mt={2}>
                <TextField
                  label="Validade do token (dias)"
                  type="number"
                  value={expirationDays}
                  onChange={(event) =>
                    setExpirationDays(Number(event.target.value))
                  }
                  inputProps={{ min: 1, max: 365 }}
                  helperText="Aplicado na próxima renovação do link."
                  style={{ width: 260 }}
                  disabled={!canManage}
                  FormHelperTextProps={{
                    style: {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'unset',
                    },
                  }}
                />
              </Box>
            </Box>

            {config && (
              <>
                <Box className={classes.settingsCard} mb={3}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        className={classes.settingLabel}
                      >
                        Link de acesso
                      </Typography>
                      <Typography className={classes.warningText}>
                        Ao gerar novo link, o link atual será invalidado
                        imediatamente.
                      </Typography>
                    </Box>
                    <Permitted tag={managePermission}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={handleRegenerateToken}
                        disabled={isSaving}
                      >
                        Gerar novo link
                      </Button>
                    </Permitted>
                  </Box>

                  <Box className={classes.linkBox}>
                    <Typography className={classes.linkText}>
                      {portalLink}
                    </Typography>
                    <Tooltip title="Copiar link">
                      <IconButton size="small" onClick={handleCopyLink}>
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box className={classes.settingsCard} mb={3}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        className={classes.settingLabel}
                      >
                        Domínios com acesso
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Qualquer colaborador com e-mail nestes domínios poderá
                        acessar o portal
                      </Typography>
                    </Box>
                    <Permitted tag={managePermission}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setOpenAddDomain(true)}
                      >
                        Adicionar domínio
                      </Button>
                    </Permitted>
                  </Box>

                  <TableContainer component={Paper}>
                    <Table
                      size="small"
                      emptyMessage="Nenhum domínio cadastrado"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Domínio</TableCell>
                          <Permitted tag={managePermission}>
                            <TableCell align="right">Ações</TableCell>
                          </Permitted>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {domains.map((domain) => (
                          <TableRow key={domain.id}>
                            <TableCell>{domain.domain}</TableCell>
                            <Permitted tag={managePermission}>
                              <TableCell align="right">
                                <Button
                                  color="secondary"
                                  size="small"
                                  startIcon={<DeleteIcon fontSize="small" />}
                                  onClick={() => setDomainToRemove(domain)}
                                >
                                  Remover
                                </Button>
                              </TableCell>
                            </Permitted>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box className={classes.settingsCard}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        className={classes.settingLabel}
                      >
                        E-mails com acesso
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Somente estes e-mails poderão acessar o portal
                      </Typography>
                    </Box>
                    <Permitted tag={managePermission}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setOpenAddEmail(true)}
                      >
                        Adicionar e-mail
                      </Button>
                    </Permitted>
                  </Box>

                  <TableContainer component={Paper}>
                    <Table size="small" emptyMessage="Nenhum e-mail cadastrado">
                      <TableHead>
                        <TableRow>
                          <TableCell>E-mail</TableCell>
                          <Permitted tag={managePermission}>
                            <TableCell align="right">Ações</TableCell>
                          </Permitted>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {emails.map((email) => (
                          <TableRow key={email.id}>
                            <TableCell>{email.email}</TableCell>
                            <Permitted tag={managePermission}>
                              <TableCell align="right">
                                <Button
                                  color="secondary"
                                  size="small"
                                  startIcon={<DeleteIcon fontSize="small" />}
                                  onClick={() => setEmailToRemove(email)}
                                >
                                  Remover
                                </Button>
                              </TableCell>
                            </Permitted>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </>
        )}
      </BaseDialog>

      <BaseDialog
        open={openAddEmail}
        setOpen={setOpenAddEmail}
        title="Adicionar e-mail com acesso"
        closeButtonText="Cancelar"
        actionButtonText="Adicionar"
        actionButton={handleAddEmail}
        disableButton={!newEmail.trim() || isSaving}
        dialogSize="sm"
        fullWidth
      >
        <Box pt={1} pb={1}>
          <TextField
            fullWidth
            label="E-mail"
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleAddEmail()
            }}
            autoFocus
          />
        </Box>
      </BaseDialog>

      <BaseDialog
        open={openAddDomain}
        setOpen={setOpenAddDomain}
        title="Adicionar domínio com acesso"
        closeButtonText="Cancelar"
        actionButtonText="Adicionar"
        actionButton={handleAddDomain}
        disableButton={!newDomain.trim() || isSaving}
        dialogSize="sm"
        fullWidth
      >
        <Box pt={1} pb={1}>
          <TextField
            fullWidth
            label="Domínio"
            placeholder="dponet.com.br"
            value={newDomain}
            onChange={(event) => setNewDomain(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleAddDomain()
            }}
            helperText="Informe o domínio sem o @ (ex.: dponet.com.br)."
            autoFocus
          />
        </Box>
      </BaseDialog>

      <ConfirmationDialog
        open={Boolean(domainToRemove)}
        setOpen={(value) => {
          if (!value) setDomainToRemove(null)
        }}
        variant="error"
        accentColor={theme.palette.error.main}
        hiddenIcon
        textButton="Remover"
        textButtonCancel="Cancelar"
        actionAcceptButton={handleConfirmRemoveDomain}
        text={
          <Box>
            <Typography variant="h3" gutterBottom>
              Remover domínio com acesso?
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Ao remover o domínio <strong>{domainToRemove?.domain}</strong>,
              todos os colaboradores que acessam o portal por meio dele perderão
              o acesso. Os e-mails cadastrados individualmente não são afetados.
            </Typography>
          </Box>
        }
      />

      <ConfirmationDialog
        open={Boolean(emailToRemove)}
        setOpen={(value) => {
          if (!value) setEmailToRemove(null)
        }}
        variant="error"
        accentColor={theme.palette.error.main}
        hiddenIcon
        textButton="Remover"
        textButtonCancel="Cancelar"
        actionAcceptButton={handleConfirmRemoveEmail}
        text={
          <Box>
            <Typography variant="h3" gutterBottom>
              Remover e-mail com acesso?
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Ao remover o e-mail <strong>{emailToRemove?.email}</strong>, o
              colaborador perderá o acesso ao portal, exceto se o domínio do
              e-mail estiver cadastrado.
            </Typography>
          </Box>
        }
      />
    </>
  )
}

export default ExternalLinkDialog
